import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { 
  ArrowLeft, Camera, Shield, CheckCircle2, AlertTriangle, 
  RefreshCw, Upload, X, Hand
} from 'lucide-react';
import BottomNav from '../components/BottomNav';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const poseInstructions = {
  peace_sign: { icon: '✌️', text: 'Hold up a peace sign (V with two fingers) next to your face' },
  thumbs_up: { icon: '👍', text: 'Give a thumbs up next to your face' },
  wave: { icon: '👋', text: 'Wave your hand next to your face' },
  pointing: { icon: '👆', text: 'Point to your chin with your index finger' }
};

const Verify = () => {
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const [selfie, setSelfie] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [step, setStep] = useState(1); // 1: instructions, 2: capture, 3: review
  const [pose, setPose] = useState(null);
  const [loadingPose, setLoadingPose] = useState(false);
  const fileInputRef = useRef(null);
  const videoRef = useRef(null);
  const [stream, setStream] = useState(null);
  const [cameraError, setCameraError] = useState(null);

  // Get random pose on mount
  useEffect(() => {
    fetchPose();
  }, []);

  const fetchPose = async () => {
    setLoadingPose(true);
    try {
      const response = await axios.get(`${API}/verification/pose`);
      setPose(response.data.pose);
    } catch (error) {
      // Default to peace_sign if fetch fails
      setPose('peace_sign');
    } finally {
      setLoadingPose(false);
    }
  };

  // Start camera for selfie
  const startCamera = async () => {
    try {
      setCameraError(null);
      const mediaStream = await navigator.mediaDevices.getUserMedia({ 
        video: { facingMode: 'user', width: 640, height: 480 } 
      });
      setStream(mediaStream);
      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
      }
      setStep(2);
    } catch (error) {
      console.error('Camera error:', error);
      setCameraError('Could not access camera. Please allow camera access or upload a photo instead.');
    }
  };

  // Stop camera
  const stopCamera = () => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
    }
  };

  // Capture selfie from video
  const captureSelfie = () => {
    if (!videoRef.current) return;
    
    const canvas = document.createElement('canvas');
    canvas.width = videoRef.current.videoWidth;
    canvas.height = videoRef.current.videoHeight;
    const ctx = canvas.getContext('2d');
    
    // Mirror the image (front camera is mirrored)
    ctx.translate(canvas.width, 0);
    ctx.scale(-1, 1);
    ctx.drawImage(videoRef.current, 0, 0);
    
    const dataUrl = canvas.toDataURL('image/jpeg', 0.8);
    setSelfie(dataUrl);
    stopCamera();
    setStep(3);
  };

  // Handle file upload
  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be less than 5MB');
      return;
    }
    
    const reader = new FileReader();
    reader.onload = () => {
      setSelfie(reader.result);
      setStep(3);
    };
    reader.readAsDataURL(file);
  };

  // Retake photo
  const retake = () => {
    setSelfie(null);
    setStep(1);
  };

  // Submit verification
  const submitVerification = async () => {
    if (!selfie || !pose) return;
    
    setSubmitting(true);
    try {
      await axios.post(`${API}/verification/submit`, {
        pose_type: pose,
        selfie_data: selfie
      });
      
      toast.success('Verification submitted! We will review it shortly.');
      await refreshUser();
      navigate('/profile');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit verification');
    } finally {
      setSubmitting(false);
    }
  };

  // Check if already verified
  if (user?.is_verified) {
    return (
      <div className="min-h-screen bg-[#050505] flex flex-col">
        <header className="glass">
          <div className="flex items-center gap-3 px-4 py-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate(-1)}
              className="text-white hover:bg-white/10"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="text-lg font-bold text-white">Profile Verification</h1>
          </div>
        </header>
        
        <div className="flex-1 flex flex-col items-center justify-center p-6 text-center">
          <div className="w-20 h-20 rounded-full bg-[#08D9D6]/20 flex items-center justify-center mb-4">
            <CheckCircle2 className="w-10 h-10 text-[#08D9D6]" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-2">Already Verified!</h2>
          <p className="text-[#A1A1AA] mb-6">Your profile has been verified. The verified badge is visible on your profile.</p>
          <Button onClick={() => navigate('/profile')} className="bg-[#08D9D6] hover:bg-[#08D9D6]/80 text-black">
            View Profile
          </Button>
        </div>
      </div>
    );
  }

  // Check if pending verification
  if (user?.verification_status === 'pending') {
    return (
      <div className="min-h-screen bg-[#050505] flex flex-col">
        <header className="glass">
          <div className="flex items-center gap-3 px-4 py-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate(-1)}
              className="text-white hover:bg-white/10"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="text-lg font-bold text-white">Profile Verification</h1>
          </div>
        </header>
        
        <div className="flex-1 flex flex-col items-center justify-center p-6 text-center">
          <div className="w-20 h-20 rounded-full bg-[#FFD700]/20 flex items-center justify-center mb-4">
            <RefreshCw className="w-10 h-10 text-[#FFD700] animate-spin" style={{ animationDuration: '3s' }} />
          </div>
          <h2 className="text-2xl font-bold text-white mb-2">Verification Pending</h2>
          <p className="text-[#A1A1AA] mb-6">Your verification is being reviewed. This usually takes less than 24 hours.</p>
          <Button onClick={() => navigate('/profile')} variant="outline" className="border-white/20 text-white">
            Back to Profile
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] flex flex-col">
      {/* Header */}
      <header className="glass">
        <div className="flex items-center gap-3 px-4 py-3">
          <Button
            data-testid="back-btn"
            variant="ghost"
            size="icon"
            onClick={() => {
              stopCamera();
              navigate(-1);
            }}
            className="text-white hover:bg-white/10"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-[#08D9D6]" />
            <h1 className="text-lg font-bold text-white">Get Verified</h1>
          </div>
        </div>
      </header>

      <div className="flex-1 flex flex-col p-6">
        {/* Step 1: Instructions */}
        {step === 1 && (
          <div className="flex-1 flex flex-col">
            <div className="text-center mb-8">
              <div className="w-20 h-20 rounded-full bg-[#08D9D6]/20 flex items-center justify-center mx-auto mb-4">
                <Shield className="w-10 h-10 text-[#08D9D6]" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-2">Verify Your Profile</h2>
              <p className="text-[#A1A1AA]">Get a verified badge to show you are real</p>
            </div>

            <div className="glass rounded-xl p-6 mb-6">
              <h3 className="text-lg font-semibold text-white mb-4">How it works:</h3>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-[#FF2E63]/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-[#FF2E63] text-sm font-bold">1</span>
                  </div>
                  <p className="text-[#A1A1AA]">Take a selfie matching the pose we show you</p>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-[#FF2E63]/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-[#FF2E63] text-sm font-bold">2</span>
                  </div>
                  <p className="text-[#A1A1AA]">Our team reviews your selfie against your profile photos</p>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-[#FF2E63]/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-[#FF2E63] text-sm font-bold">3</span>
                  </div>
                  <p className="text-[#A1A1AA]">Get verified within 24 hours and show the badge!</p>
                </li>
              </ul>
            </div>

            {/* Required Pose */}
            {pose && (
              <div className="glass rounded-xl p-6 mb-6 border-2 border-[#FF2E63]/50 bg-[#FF2E63]/5">
                <h3 className="text-lg font-semibold text-white mb-3 text-center">Your Required Pose:</h3>
                <div className="text-center">
                  <span className="text-6xl mb-3 block">{poseInstructions[pose]?.icon}</span>
                  <p className="text-[#EDEDED] font-medium">{poseInstructions[pose]?.text}</p>
                </div>
              </div>
            )}

            <div className="glass rounded-xl p-4 mb-6 border border-[#08D9D6]/30 bg-[#08D9D6]/5">
              <p className="text-sm text-[#08D9D6] flex items-start gap-2">
                <Shield className="w-5 h-5 flex-shrink-0 mt-0.5" />
                <span>Your selfie is only used for verification and is deleted after review. It is never shown publicly.</span>
              </p>
            </div>

            {cameraError && (
              <div className="glass rounded-xl p-4 mb-6 border border-[#FFD700]/30 bg-[#FFD700]/5">
                <p className="text-sm text-[#FFD700] flex items-start gap-2">
                  <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                  <span>{cameraError}</span>
                </p>
              </div>
            )}

            <div className="mt-auto space-y-3">
              <Button
                data-testid="start-camera-btn"
                onClick={startCamera}
                className="w-full py-6 text-lg bg-[#FF2E63] hover:bg-[#E01E50]"
              >
                <Camera className="w-5 h-5 mr-2" />
                Take Selfie
              </Button>
              
              <input
                type="file"
                accept="image/*"
                ref={fileInputRef}
                onChange={handleFileUpload}
                className="hidden"
              />
              <Button
                data-testid="upload-photo-btn"
                onClick={() => fileInputRef.current?.click()}
                variant="outline"
                className="w-full py-6 text-lg border-white/20 text-white hover:bg-white/10"
              >
                <Upload className="w-5 h-5 mr-2" />
                Upload Photo Instead
              </Button>
            </div>
          </div>
        )}

        {/* Step 2: Camera Capture */}
        {step === 2 && (
          <div className="flex-1 flex flex-col">
            <div className="text-center mb-4">
              <h2 className="text-xl font-bold text-white mb-2">Hold your phone at eye level</h2>
              <p className="text-[#A1A1AA]">Make sure your face is clearly visible</p>
            </div>

            <div className="relative flex-1 max-h-[60vh] rounded-xl overflow-hidden bg-black mb-6">
              <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                className="w-full h-full object-cover"
                style={{ transform: 'scaleX(-1)' }}
              />
              {/* Face guide overlay */}
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div className="w-48 h-64 border-2 border-dashed border-[#08D9D6]/50 rounded-full" />
              </div>
            </div>

            <div className="flex gap-3">
              <Button
                onClick={() => {
                  stopCamera();
                  setStep(1);
                }}
                variant="outline"
                className="flex-1 py-6 border-white/20 text-white"
              >
                Cancel
              </Button>
              <Button
                data-testid="capture-btn"
                onClick={captureSelfie}
                className="flex-1 py-6 bg-[#FF2E63] hover:bg-[#E01E50]"
              >
                <Camera className="w-5 h-5 mr-2" />
                Capture
              </Button>
            </div>
          </div>
        )}

        {/* Step 3: Review & Submit */}
        {step === 3 && (
          <div className="flex-1 flex flex-col">
            <div className="text-center mb-4">
              <h2 className="text-xl font-bold text-white mb-2">Looking good!</h2>
              <p className="text-[#A1A1AA]">Make sure your face is clearly visible</p>
            </div>

            <div className="relative flex-1 max-h-[60vh] rounded-xl overflow-hidden bg-black mb-6">
              {selfie && (
                <img
                  src={selfie}
                  alt="Verification selfie"
                  className="w-full h-full object-cover"
                />
              )}
              <button
                onClick={retake}
                className="absolute top-3 right-3 p-2 bg-black/50 rounded-full hover:bg-black/70"
              >
                <X className="w-5 h-5 text-white" />
              </button>
            </div>

            <div className="flex gap-3">
              <Button
                onClick={retake}
                variant="outline"
                className="flex-1 py-6 border-white/20 text-white"
              >
                <RefreshCw className="w-5 h-5 mr-2" />
                Retake
              </Button>
              <Button
                data-testid="submit-verification-btn"
                onClick={submitVerification}
                disabled={submitting}
                className="flex-1 py-6 bg-[#08D9D6] hover:bg-[#08D9D6]/80 text-black"
              >
                {submitting ? (
                  <>
                    <RefreshCw className="w-5 h-5 mr-2 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <CheckCircle2 className="w-5 h-5 mr-2" />
                    Submit
                  </>
                )}
              </Button>
            </div>
          </div>
        )}
      </div>

      <BottomNav />
    </div>
  );
};

export default Verify;
